import {
  Box,
  Container,
  IconButton,
  InputLabel,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DateDropZone from '../DropZones/DateDropZone';
import DatePicker from '../../DatePicker/DatePicker';
import { resetSource, setSource } from '../../../redux/Calculations/CalculationsActions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fontSize } from '../../..';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import { CanvasSelect } from '../common/CanvasSelect';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
interface IDateToNameOrNumberPipe {
  flow: string;
  propKey: string;
  lengthOfSource: number;
  isFlowUsedInOtherFlowsAsSource: boolean;
}

type ParseOption = {
  value: 'day' | 'month' | 'year';
  label: string;
};

const DateToNameOrNumberPipe = ({
  propKey,
  lengthOfSource,
  flow,
  isFlowUsedInOtherFlowsAsSource,
}: IDateToNameOrNumberPipe) => {
  const { calculations, tablesForSelectedDataSets, chartProperties } = useSelector(
    (state: RootState) => {
      return {
        calculations: state.calculations,
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
        chartProperties: state.chartProperties,
      };
    },
  );
  const currCalculation = calculations.properties[propKey];
  const parseTo: ParseOption[] = useMemo(() => {
    if (flow === 'datePartName') {
      return [
        {
          value: 'day',
          label: 'Day',
        },
        {
          value: 'month',
          label: 'Month',
        },
      ];
    } else
      return [
        {
          value: 'day',
          label: 'Day',
        },
        {
          value: 'month',
          label: 'Month',
        },
        {
          value: 'year',
          label: 'Year',
        },
      ];
  }, [flow]);
  const valueTypes = ['text', 'decimal', 'integer', 'boolean', 'date'];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);
  const dispatch = useDispatch();

  const currentCalculationSession = currCalculation.currentCalculationSession;

  const activeFlow = currCalculation.currentCalculationSession?.activeFlow ?? null;
  const calculationInfo = currCalculation.currentCalculationSession?.calculationInfo ?? null;
  const source = activeFlow ? calculationInfo?.flows[activeFlow][0].source : null;
  const sourceType = activeFlow ? calculationInfo?.flows[activeFlow][0]!.sourceType : null;
  const selectedOption = source ? (parseTo.find((t) => t.value === source[1]) ?? null) : null;

  const getName = (): string | null => {
    if (!currentCalculationSession || !sourceType || !source || !calculationInfo) return null;
    if (sourceType[0] === 'field') return calculationInfo.fields[source[0]].displayName;
    else if (sourceType[0] === 'flow') return currentCalculationSession.flowDisplayNames[source[0]];
    return source[0];
  };

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  useEffect(() => {
    if (!sourceType || !source||sourceType[0] !== 'field') return;
    

    (async () => {
      try {
        const field = calculationInfo?.fields?.[source[0]];
        if (!field) return;
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, [source?.[0]]);

  if (
    !currentCalculationSession ||
    !currentCalculationSession.calculationInfo ||
    !activeFlow ||
    !source ||
    !sourceType
  )
    return null;

  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        margin: 0,
      }}
      maxWidth="xs"
    >
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily:'Axiforma Black'
              fontSize: fontSize.large,
              paddingLeft: '8px',
            }}
          >
            {flow === 'datePartName' ? 'Date Name' : 'Date Number'}
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source?.length > 0 ? 1 : 0} of 1/1)
          </Typography>
        </Box>
        <Tooltip
          title="Add Custom Date"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
            disabled={isFlowUsedInOtherFlowsAsSource}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          {valueTypes.map((type, index) => {
            return (
              <MenuItem
                key={index}
                sx={{ width: '7rem' }}
                disabled
                value={type}
                onClick={() => {
                  setAnchorEl(null);
                  dispatch(
                    setSource(
                      propKey,
                      activeFlow,
                      0,
                      new Date().toISOString().split('T')[0],
                      'date',
                      0,
                    ),
                  );
                  dispatch(setSource(propKey, activeFlow, 0, 'month', 'text', 1));
                }}
              >
                {type.replace(/\b\w/g, (char) => char.toUpperCase())}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      <DateDropZone
        propKey={propKey}
        flow={flow}
        disableDrop={isFlowUsedInOtherFlowsAsSource}
        sx={{
          ...(source[0] ? { minHeight: '5rem', flexGrow: 0 } : {}),
        }}
      >
        {source.length === 0 ? (
          <p
            style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px', textAlign: 'center' }}
          >
            Drop a column here
          </p>
        ) : (
          <>
            <ListItem
              className="axisFilterField"
              sx={{
                borderRadius: '5px',
                border: isShowWarning ? '1px solid #850000ff !important' : '',
                marginBlock: '5px',
                marginLeft: '0.5rem',
                paddingLeft: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '24px', // Match this height with TextField height if needed
                width: '95%',
                gap: '0.2rem',
              }}
            >
              {!isFlowUsedInOtherFlowsAsSource && (
                <button
                  type="button"
                  className="buttonCommon columnClose"
                  onClick={() => {
                    dispatch(resetSource(propKey, activeFlow, 0));
                  }}
                  title="Remove field"
                >
                  <CloseRoundedIcon style={{ fontSize: '13px' }} />
                </button>
              )}
              {isFlowUsedInOtherFlowsAsSource && (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              )}
              {sourceType[0] === 'date' ? (
                <DatePicker
                  value={source[0]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        newValue.toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                  }}
                  disabled={isFlowUsedInOtherFlowsAsSource}
                />
              ) : sourceType[0] === 'field' || sourceType[0] === 'flow' ? (
                <ListItemText
                  primary={getName()}
                  sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
                />
              ) : null}
            </ListItem>
          </>
        )}
      </DateDropZone>
      {source.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              cursor: 'pointer',
              textAlign: 'left',
              paddingBottom: '10px',
              minHeight: '4rem',
              marginTop: '0',
            }}
          >
            <InputLabel
              sx={{
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                fontSize: fontSize.medium,
                // color: "gray",
              }}
            >
              {flow === 'datePartName' ? 'Parse as Name' : 'Parse as Number'}
            </InputLabel>

            <CanvasSelect<ParseOption>
              value={selectedOption}
              options={parseTo}
              placeholder="Select"
              getLabel={(opt) => opt.label}
              getKey={(opt) => opt.value}
              onChange={(opt) => dispatch(setSource(propKey, activeFlow, 0, opt.value, 'text', 1))}
              disabled={isFlowUsedInOtherFlowsAsSource}
              style={{
                width: '95%',
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                height: '2rem',
                fontSize: '11px',
              }}
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default DateToNameOrNumberPipe;
